import { useState, useMemo } from 'react';
import { useAnalysisStore } from '../stores/useAnalysisStore';
import { useOrderStore } from '../stores/useOrderStore';
import { useNavigate } from 'react-router-dom';
import { COURIERS, DIMENSION_CONFIG } from '@shipping/core';
import type { RoutingDimension, CourierId, RoutingResult } from '@shipping/core';

const DIMENSION_OPTIONS: Array<{ value: RoutingDimension | 'all'; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'speed', label: '速度优先' },
  { value: 'price', label: '价格优先' },
  { value: 'rating', label: '评价优先' },
  { value: 'composite', label: '综合最优' },
];

const PAGE_SIZE = 20;

const DIMENSION_ICONS: Record<RoutingDimension, string> = {
  speed: '⚡', price: '💰', rating: '⭐', composite: '👑',
};

function courierName(id: string): string {
  return COURIERS[id as CourierId]?.name ?? id;
}

function courierColor(id: string): string {
  return COURIERS[id as CourierId]?.color ?? '#666';
}

export default function MicroRoutingPage() {
  const navigate = useNavigate();
  const micro = useAnalysisStore((s) => s.micro);
  const parsedOrders = useOrderStore((s) => s.parsedOrders);
  const [dimensionFilter, setDimensionFilter] = useState<RoutingDimension | 'all'>('all');
  const [onlySwaps, setOnlySwaps] = useState(false);
  const [page, setPage] = useState(0);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // No data — show prompt
  if (!micro || micro.results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="text-6xl">📋</div>
        <h2 className="text-xl font-semibold text-gray-700">暂无策略数据</h2>
        <p className="text-gray-400">请先上传订单数据并完成分析</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2.5 bg-brand text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          去上传数据
        </button>
      </div>
    );
  }

  // Build order lookup
  const orderMap = useMemo(() => {
    const map = new Map<string, typeof parsedOrders[0]>();
    for (const o of parsedOrders) map.set(o.id, o);
    return map;
  }, [parsedOrders]);

  // Filter results
  const activeDimension: RoutingDimension = dimensionFilter === 'all' ? 'composite' : dimensionFilter;

  const filteredResults = useMemo(() => {
    let results = micro.results;

    if (onlySwaps) {
      results = results.filter(r =>
        r.currentCourier && r.recommendations[activeDimension] !== r.currentCourier
      );
    }

    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      results = results.filter(r => {
        const order = orderMap.get(r.orderId);
        if (!order) return false;
        return (
          order.destination.raw.toLowerCase().includes(q) ||
          order.category.toLowerCase().includes(q) ||
          courierName(r.currentCourier ?? '').includes(q) ||
          courierName(r.recommendations[activeDimension]).includes(q)
        );
      });
    }

    return results;
  }, [micro.results, onlySwaps, activeDimension, searchText, orderMap]);

  const totalPages = Math.ceil(filteredResults.length / PAGE_SIZE);
  const pageResults = filteredResults.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when filter changes
  const handleFilterChange = (dim: RoutingDimension | 'all') => {
    setDimensionFilter(dim);
    setPage(0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">策略推荐</h1>
          <p className="text-sm text-gray-500 mt-1">
            逐单四维评分推荐，找到每一单的最优快递
          </p>
        </div>
      </div>

      {/* Summary bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-6 sm:gap-8">
        <div>
          <span className="text-sm text-gray-500">总计: </span>
          <span className="font-semibold">{micro.totalOrders.toLocaleString()} 单</span>
        </div>
        <div className="h-4 w-px bg-gray-200" />
        <div>
          <span className="text-sm text-gray-500">建议换快递: </span>
          <span className="font-semibold text-warning">
            {micro.suggestedSwaps.toLocaleString()} 单
            ({(micro.swapRatio * 100).toFixed(1)}%)
          </span>
        </div>
        <div className="h-4 w-px bg-gray-200" />
        <div>
          <span className="text-sm text-gray-500">预计节省: </span>
          <span className="font-semibold text-success">
            ¥{micro.estimatedSavings.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">推荐维度:</span>
          <div className="flex gap-1">
            {DIMENSION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleFilterChange(opt.value)}
                className={[
                  'px-3 py-1.5 text-sm rounded-md transition-colors',
                  dimensionFilter === opt.value
                    ? 'bg-brand text-white'
                    : 'text-gray-600 hover:bg-gray-100',
                ].join(' ')}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-4 w-px bg-gray-200" />
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={onlySwaps}
            onChange={(e) => { setOnlySwaps(e.target.checked); setPage(0); }}
            className="rounded border-gray-300"
          />
          仅看建议更换
        </label>
        <div className="h-4 w-px bg-gray-200" />
        <input
          type="text"
          placeholder="搜索地址/品类/快递..."
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-brand w-48"
        />
        <span className="ml-auto text-sm text-gray-400">
          {filteredResults.length} 条结果
        </span>
      </div>

      {/* Results table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 w-14">#</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">收货地址</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 w-20">重量</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 w-24">品类</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600 w-24">当前快递</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600 w-24">推荐快递</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 w-20">节省</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600 w-16">评分</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageResults.map((result, idx) => {
                const order = orderMap.get(result.orderId);
                if (!order) return null;
                const recCourier = result.recommendations[activeDimension];
                const saving = result.savings[activeDimension];
                const isSwap = result.currentCourier && recCourier !== result.currentCourier;
                const score = result.scores[recCourier as CourierId]?.[activeDimension] ?? 0;
                const isExpanded = expandedRow === result.orderId;

                return (
                  <RowGroup key={result.orderId}>
                    <tr
                      className={[
                        'hover:bg-gray-50 cursor-pointer transition-colors',
                        isSwap ? 'bg-amber-50/40' : '',
                      ].join(' ')}
                      onClick={() => setExpandedRow(isExpanded ? null : result.orderId)}
                    >
                      <td className="px-4 py-3 text-gray-400 tabular-nums">
                        {page * PAGE_SIZE + idx + 1}
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={order.destination.raw}>
                        {order.destination.raw}
                        {order.destination.isBSGD && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded">北上广深</span>
                        )}
                        {order.destination.isRemote && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">偏远</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                        {order.weight}kg
                      </td>
                      <td className="px-4 py-3 text-gray-600">{order.category}</td>
                      <td className="px-4 py-3 text-center">
                        {result.currentCourier ? (
                          <span
                            className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white"
                            style={{ backgroundColor: courierColor(result.currentCourier) }}
                          >
                            {courierName(result.currentCourier)}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white"
                          style={{ backgroundColor: courierColor(recCourier) }}
                        >
                          {courierName(recCourier)}
                        </span>
                        {isSwap && <span className="ml-1 text-warning text-xs">↗</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {saving > 0 ? (
                          <span className="text-success font-medium">¥{saving.toFixed(1)}</span>
                        ) : saving < 0 ? (
                          <span className="text-danger">+¥{Math.abs(saving).toFixed(1)}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <ScoreBadge score={score} />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50/70">
                        <td colSpan={8} className="px-4 py-4">
                          <ExpandedDetail result={result} activeDimension={activeDimension} />
                        </td>
                      </tr>
                    )}
                  </RowGroup>
                );
              })}
              {pageResults.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    没有符合筛选条件的订单
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              第 {page + 1} / {totalPages} 页 · 共 {filteredResults.length} 条
            </span>
            <div className="flex gap-1">
              <button
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
                className="px-3 py-1 text-sm rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              {/* Page number buttons */}
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 7) {
                  pageNum = i;
                } else if (page < 3) {
                  pageNum = i;
                } else if (page > totalPages - 4) {
                  pageNum = totalPages - 7 + i;
                } else {
                  pageNum = page - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={[
                      'w-8 h-8 text-sm rounded',
                      page === pageNum
                        ? 'bg-brand text-white'
                        : 'border border-gray-200 hover:bg-gray-50',
                    ].join(' ')}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1 text-sm rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Champions */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-500 mb-3">四维冠军统计</h3>
        <div className="flex flex-wrap gap-6">
          {(['speed', 'price', 'rating', 'composite'] as RoutingDimension[]).map((dim) => {
            const champ = micro.champions[dim];
            if (!champ) return null;
            const config = DIMENSION_CONFIG[dim];
            return (
              <div key={dim}>
                <span className="text-sm text-gray-400">
                  {DIMENSION_ICONS[dim]} {config.label}:
                </span>{' '}
                <span
                  className="font-semibold"
                  style={{ color: courierColor(champ.courierId) }}
                >
                  {courierName(champ.courierId)}
                </span>{' '}
                <span className="text-xs text-gray-400">
                  ({champ.orderCount.toLocaleString()}单)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RowGroup({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 80 ? 'text-success' : score >= 60 ? 'text-brand' : score >= 40 ? 'text-warning' : 'text-danger';
  return (
    <span className={`${color} font-mono font-medium text-xs`}>
      {Math.round(score)}
    </span>
  );
}

function ExpandedDetail({
  result,
  activeDimension,
}: {
  result: RoutingResult;
  activeDimension: RoutingDimension;
}) {
  const dims: RoutingDimension[] = ['speed', 'price', 'rating', 'composite'];

  return (
    <div className="space-y-3">
      {/* Rationale */}
      <p className="text-sm text-gray-600">
        <span className="font-medium text-gray-700">决策理由:</span>{' '}
        {result.rationale}
      </p>

      {/* Per-courier scores */}
      <div className="overflow-x-auto">
        <table className="text-xs w-full">
          <thead>
            <tr className="text-gray-400">
              <th className="text-left py-1 pr-4 font-medium">快递公司</th>
              {dims.map((d) => (
                <th key={d} className="text-center py-1 px-3 font-medium">
                  {DIMENSION_ICONS[d]} {DIMENSION_CONFIG[d].label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(result.scores).map(([courierId, dimScores]) => {
              const isRec = result.recommendations[activeDimension] === courierId;
              return (
                <tr
                  key={courierId}
                  className={isRec ? 'bg-blue-50/60' : ''}
                >
                  <td className="py-1 pr-4">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1.5"
                      style={{ backgroundColor: courierColor(courierId) }}
                    />
                    <span className={isRec ? 'font-semibold' : ''}>
                      {courierName(courierId)}
                    </span>
                    {isRec && <span className="ml-1 text-brand text-[10px]">推荐</span>}
                    {result.currentCourier === courierId && (
                      <span className="ml-1 text-gray-400 text-[10px]">当前</span>
                    )}
                  </td>
                  {dims.map((d) => {
                    const s = dimScores[d] ?? 0;
                    const isWinner = result.recommendations[d] === courierId;
                    return (
                      <td key={d} className="text-center py-1 px-3">
                        <span className={isWinner ? 'font-bold text-brand' : 'text-gray-500'}>
                          {Math.round(s)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
